import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { useDynamicLocale } from '@/hoslog-intl';
import { z } from 'zod';
import { useManageDiscountCategoryDetail } from '../hooks/useManageDiscountCategoryDetail';

const manageDiscountFormSchema = z
  .object({
    fromAmount: z.string().trim().min(1, {
      message: 'common.form.validation.required',
    }),

    toAmount: z.string().trim().min(1, {
      message: 'common.form.validation.required',
    }),

    discountType: z.string().min(1, {
      message: 'common.form.validation.required',
    }),

    value: z.string().trim().min(1, {
      message: 'common.form.validation.required',
    }),

    minValue: z.string().trim().optional(),
    maxValue: z.string().trim().optional(),
  })
  .superRefine((data, ctx) => {
    const fromAmount = Number(data.fromAmount);
    const toAmount = Number(data.toAmount);
    const value = Number(data.value);

    if (data.fromAmount && data.toAmount && fromAmount >= toAmount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['toAmount'],
        message: 'To amount must be greater than from amount',
      });
    }

    if (data.discountType === 'Percentage') {
      if (!data.minValue || data.minValue.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['minValue'],
          message: 'common.form.validation.required',
        });
      }

      if (!data.maxValue || data.maxValue.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['maxValue'],
          message: 'common.form.validation.required',
        });
      }

      if (value < 0 || value > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['value'],
          message: 'Percentage must be between 0 and 100',
        });
      }
    }
  });

const discounttype = {
  FLAT: 'Flat',
  PERCENTAGE: 'Percentage',
};

interface ManageDiscountCategorySlabFormProps {
  discountCategoryId: string;
  discountCategoryDetailId?: string;
  defaultValues?: {
    fromAmount: string;
    toAmount: string;
    discountType: string;
    value: string;
    minValue?: string;
    maxValue?: string;
  };
}

export const ManageDiscountCategorySlabForm = ({
  discountCategoryId,
  discountCategoryDetailId,
  defaultValues,
}: ManageDiscountCategorySlabFormProps) => {
  const { mutateAsync } = useManageDiscountCategoryDetail();

  const form = useForm({
    schema: manageDiscountFormSchema,
    mode: 'onBlur',
    defaultValues,
  });

  const { t } = useDynamicLocale();
  const discountTypeOption = [
    { label: t('configurationManagement.discountManagement.category.manageDiscountCategory.flat'), value: 'Flat' },
    {
      label: t('configurationManagement.discountManagement.category.manageDiscountCategory.percentage'),
      value: 'Percentage',
    },
  ];

  const discountType = form.watch('discountType');

  return (
    <Form
      form={form}
      onSubmit={async (values) => {
        await mutateAsync({
          discountCategoryId,
          discountCategoryDetailId: discountCategoryDetailId ?? '',
          fromAmount: Number(values.fromAmount),
          toAmount: Number(values.toAmount),
          discountType: values.discountType,
          value: Number(values.value),
          minValue: Number(values.minValue),
          maxValue: Number(values.maxValue),
        });
      }}
    >
      <div className="flex flex-col gap-4.5">
        <div className="grid grid-cols-2 gap-4.5">
          <Input
            label="configurationManagement.discountManagement.category.manageDiscountCategory.fromPlaceholder"
            {...form.register('fromAmount')}
            placeholder={t(
              'configurationManagement.discountManagement.category.manageDiscountCategory.fromPlaceholder',
            )}
            forceRequired
            inputMode="numeric"
            type="text"
            onlyNumbers
            maxLength={10}
          />

          <Input
            label="configurationManagement.discountManagement.category.manageDiscountCategory.toPlaceholder"
            {...form.register('toAmount')}
            placeholder={t('configurationManagement.discountManagement.category.manageDiscountCategory.toPlaceholder')}
            forceRequired
            inputMode="numeric"
            type="text"
            onlyNumbers
            maxLength={10}
          />
        </div>

        <Select
          label="configurationManagement.discountManagement.category.table.discountType"
          {...form.register('discountType')}
          placeholder={t(
            'configurationManagement.discountManagement.category.manageDiscountCategory.discountTypePlaceholder',
          )}
          options={discountTypeOption}
          forceRequired
        />

        <Input
          label="configurationManagement.discountManagement.category.table.value"
          {...form.register('value')}
          placeholder={t('configurationManagement.discountManagement.category.manageDiscountCategory.valuePlaceholder')}
          forceRequired
          inputMode="numeric"
          type="text"
          onlyNumbers
          maxLength={10}
        />

        <div className="grid grid-cols-2 gap-4.5">
          <Input
            label="configurationManagement.discountManagement.category.table.minValue"
            forceRequired={discountType === discounttype.PERCENTAGE}
            {...form.register('minValue')}
            disabled={discountType !== discounttype.PERCENTAGE}
            placeholder={t(
              'configurationManagement.discountManagement.category.manageDiscountCategory.minValuePlaceholder',
            )}
            inputMode="numeric"
            type="text"
            onlyNumbers
            maxLength={3}
          />

          <Input
            label="configurationManagement.discountManagement.category.table.maxValue"
            forceRequired={discountType === discounttype.PERCENTAGE}
            {...form.register('maxValue')}
            disabled={discountType !== discounttype.PERCENTAGE}
            placeholder={t(
              'configurationManagement.discountManagement.category.manageDiscountCategory.maxValuePlaceholder',
            )}
            inputMode="numeric"
            type="text"
            onlyNumbers
            maxLength={3}
          />
        </div>
      </div>
    </Form>
  );
};
